import React from 'react';
import { Trash2, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shares: any[];
  sharesLoading: boolean;
  shareEmail: string;
  setShareEmail: (email: string) => void;
  shareAccessLevel: 'View' | 'Edit' | 'Manage';
  setShareAccessLevel: (level: 'View' | 'Edit' | 'Manage') => void;
  onShare: () => Promise<void>;
  onUpdateShare: (shareId: string, accessLevel: 'View' | 'Edit' | 'Manage') => Promise<void>;
  onRemoveShare: (shareId: string) => Promise<void>;
}

const ShareDialog = ({
  open,
  onOpenChange,
  shares,
  sharesLoading,
  shareEmail,
  setShareEmail,
  shareAccessLevel,
  setShareAccessLevel,
  onShare,
  onUpdateShare,
  onRemoveShare
}: ShareDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-[560px]">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>Share Knowledge Base</DialogTitle>
          <DialogDescription>
            Share this knowledge base with other users.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 px-6 py-5">
          <div className="space-y-2">
            <Label htmlFor="email">Username or Email</Label>
            <div className="flex space-x-2">
              <Input 
                id="email" 
                placeholder="username or email" 
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
              />
              <Select 
                value={shareAccessLevel}
                onValueChange={(value) => setShareAccessLevel(value as 'View' | 'Edit' | 'Manage')}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Access" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="View">View</SelectItem>
                  <SelectItem value="Edit">Edit</SelectItem>
                  <SelectItem value="Manage">Manage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button 
            className="w-full" 
            onClick={onShare}
            disabled={!shareEmail}
          >
            Share
          </Button>
          
          <Separator />
          
          <div>
            <h3 className="mb-3 flex items-center text-sm font-medium">
              <Users className="mr-1 h-4 w-4" />
              Shared with
            </h3>
            
            {sharesLoading ? (
              <div className="py-2 text-center">
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            ) : shares.length > 0 ? (
              <div className="space-y-2">
                {shares.map((share) => (
                  <div key={share.id} className="flex items-center justify-between rounded-xl border border-border/70 bg-muted/20 p-3">
                    <div>
                      <p className="text-sm font-medium">{share.user_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Shared on {new Date(share.shared_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Select 
                        value={share.access_level}
                        onValueChange={(value) => onUpdateShare(share.id, value as 'View' | 'Edit' | 'Manage')}
                      >
                        <SelectTrigger className="h-8 w-[96px]">
                          <SelectValue placeholder="Access" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="View">View</SelectItem>
                          <SelectItem value="Edit">Edit</SelectItem>
                          <SelectItem value="Manage">Manage</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8 text-destructive" 
                        onClick={() => onRemoveShare(share.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border/70 py-6 text-center">
                <p className="text-sm text-muted-foreground">
                  This knowledge base hasn't been shared with anyone.
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareDialog;